import { useEffect, useState, useMemo } from "react";
import api, { errMsg, formatTL } from "@/lib/api";
import PageHeader from "@/components/app/PageHeader";
import { toast } from "sonner";
import {
  FileSpreadsheet, X, Plus, Pencil, Trash2, GraduationCap,
  TrendingUp, TrendingDown, Wallet, BarChart3, Filter, Landmark, RefreshCw, Link2, Unlink,
} from "lucide-react";

const MONTHS = ["Oca","Şub","Mar","Nis","May","Haz","Tem","Ağu","Eyl","Eki","Kas","Ara"];
const MONTHS_FULL = ["Ocak","Şubat","Mart","Nisan","Mayıs","Haziran","Temmuz","Ağustos","Eylül","Ekim","Kasım","Aralık"];

const TABS = [
  { key: "students", label: "Öğrenci Ödemeleri", icon: GraduationCap },
  { key: "expenses", label: "Okul Gideri (Tedarikçi)", icon: Wallet },
  { key: "reconcile", label: "Banka Eşleştir", icon: Landmark },
  { key: "comparison", label: "Gelir-Gider Karşılaştırma", icon: BarChart3 },
];

const emptyStudent = () => ({ student_name: "", year: new Date().getFullYear(), month: 1, amount: "", invoiced: false, notes: "" });
const emptyExp = () => ({ supplier_name: "", year: new Date().getFullYear(), month: 1, amount: "", date: "", notes: "" });

export default function SchoolPaymentsPage() {
  const [tab, setTab] = useState("students");
  const [year, setYear] = useState(new Date().getFullYear());
  const [payments, setPayments] = useState([]);
  const [expenses, setExpenses] = useState([]);
  const [summary, setSummary] = useState(null);
  const [q, setQ] = useState("");
  const [monthFilter, setMonthFilter] = useState("all");

  // Upload state
  const [showUpload, setShowUpload] = useState(false);
  const [uploadFile, setUploadFile] = useState(null);
  const [uploadPreview, setUploadPreview] = useState(null);
  const [uploadLoading, setUploadLoading] = useState(false);

  // Form state - student payment
  const [showStudentForm, setShowStudentForm] = useState(false);
  const [editingStudent, setEditingStudent] = useState(null);
  const [studentForm, setStudentForm] = useState(emptyStudent());

  // Form state - school expense
  const [showExpForm, setShowExpForm] = useState(false);
  const [editingExp, setEditingExp] = useState(null);
  const [expForm, setExpForm] = useState(emptyExp());

  // Bank reconciliation state
  const [reconcile, setReconcile] = useState(null);
  const [reconLoading, setReconLoading] = useState(false);
  const [reconSelected, setReconSelected] = useState(new Set());
  const [reconTolerance, setReconTolerance] = useState(2);

  const loadReconcile = async () => {
    setReconLoading(true);
    try {
      const { data } = await api.get(`/school-payments/reconcile-preview?year=${year}&tolerance=${reconTolerance}`);
      setReconcile(data);
      setReconSelected(new Set(data.matches.map(m => m.bank_tx_id)));
    } catch (err) { toast.error(errMsg(err)); }
    finally { setReconLoading(false); }
  };

  const applyReconcile = async () => {
    if (reconSelected.size === 0) { toast.error("En az bir eşleştirme seçin"); return; }
    if (!window.confirm(`${reconSelected.size} eşleştirme uygulanacak. Ödemeler "banka eşleşti" olarak işaretlenecek, banka hareketleri kategorisi "Öğrenci Ödemesi" olacak. Devam?`)) return;
    const chosen = (reconcile.matches || []).filter(m => reconSelected.has(m.bank_tx_id));
    try {
      const { data } = await api.post("/school-payments/reconcile-apply", { matches: chosen });
      toast.success(`${data.applied} eşleştirme uygulandı`);
      setReconcile(null); setReconSelected(new Set());
      await loadData();
    } catch (err) { toast.error(errMsg(err)); }
  };

  const toggleReconSel = (id) => {
    const s = new Set(reconSelected);
    if (s.has(id)) s.delete(id); else s.add(id);
    setReconSelected(s);
  };

  const loadData = async () => {
    try {
      const [p, e, s] = await Promise.all([
        api.get(`/school-payments?year=${year}`),
        api.get(`/school-supplier-payments?year=${year}`),
        api.get(`/school-payments/summary?year=${year}`),
      ]);
      setPayments(p.data || []);
      setExpenses(e.data || []);
      setSummary(s.data);
    } catch (err) { toast.error(errMsg(err)); }
  };

  useEffect(() => { loadData(); }, [year]);

  // Filters
  const filteredPayments = useMemo(() => {
    const query = q.toLocaleLowerCase("tr-TR");
    return payments.filter(p => {
      if (monthFilter !== "all" && p.month !== Number(monthFilter)) return false;
      if (!query) return true;
      return (p.student_name || "").toLocaleLowerCase("tr-TR").includes(query);
    });
  }, [payments, q, monthFilter]);

  const filteredExpenses = useMemo(() => {
    const query = q.toLocaleLowerCase("tr-TR");
    return expenses.filter(e => {
      if (monthFilter !== "all" && e.month !== Number(monthFilter)) return false;
      if (!query) return true;
      return (e.supplier_name || "").toLocaleLowerCase("tr-TR").includes(query);
    });
  }, [expenses, q, monthFilter]);

  // KPIs
  const g = summary?.grand || {};

  // Upload flow
  const runUploadPreview = async () => {
    if (!uploadFile) { toast.error("Dosya seçin"); return; }
    const fd = new FormData();
    fd.append("file", uploadFile);
    fd.append("preview", "true");
    setUploadLoading(true);
    try {
      const { data } = await api.post("/school-payments/upload-xlsx", fd, { headers: { "Content-Type": "multipart/form-data" } });
      setUploadPreview(data);
      toast.success(`${data.totals?.row_count || 0} kayıt okundu`);
    } catch (e) { toast.error(errMsg(e)); }
    finally { setUploadLoading(false); }
  };

  const runUploadCommit = async () => {
    if (!uploadFile) return;
    if (!window.confirm("Yüklediğiniz akademik yıllara ait mevcut kayıtlar silinip yenileriyle değiştirilecek. Devam?")) return;
    const fd = new FormData();
    fd.append("file", uploadFile);
    fd.append("preview", "false");
    setUploadLoading(true);
    try {
      const { data } = await api.post("/school-payments/upload-xlsx", fd, { headers: { "Content-Type": "multipart/form-data" } });
      toast.success(`${data.inserted} kayıt sisteme işlendi`);
      setShowUpload(false); setUploadFile(null); setUploadPreview(null);
      await loadData();
    } catch (e) { toast.error(errMsg(e)); }
    finally { setUploadLoading(false); }
  };

  // Student payment CRUD
  const openAddStudent = () => { setStudentForm({ ...emptyStudent(), year, month: 1 }); setEditingStudent(null); setShowStudentForm(true); };
  const openEditStudent = (row) => { setStudentForm({ ...row }); setEditingStudent(row); setShowStudentForm(true); };
  const submitStudent = async (e) => {
    e.preventDefault();
    try {
      if (editingStudent) await api.put(`/school-payments/${editingStudent.id}`, studentForm);
      else await api.post("/school-payments", studentForm);
      toast.success(editingStudent ? "Güncellendi" : "Eklendi");
      setShowStudentForm(false);
      await loadData();
    } catch (err) { toast.error(errMsg(err)); }
  };
  const deleteStudent = async (row) => {
    if (!window.confirm(`${row.student_name} · ${MONTHS_FULL[row.month-1]} ${row.year} · ${formatTL(row.amount)} silinecek. Onaylıyor musunuz?`)) return;
    try { await api.delete(`/school-payments/${row.id}`); toast.success("Silindi"); await loadData(); }
    catch (err) { toast.error(errMsg(err)); }
  };

  // Expense CRUD
  const openAddExp = () => { setExpForm({ ...emptyExp(), year, month: 1 }); setEditingExp(null); setShowExpForm(true); };
  const openEditExp = (row) => { setExpForm({ ...row }); setEditingExp(row); setShowExpForm(true); };
  const submitExp = async (e) => {
    e.preventDefault();
    try {
      if (editingExp) await api.put(`/school-supplier-payments/${editingExp.id}`, expForm);
      else await api.post("/school-supplier-payments", expForm);
      toast.success(editingExp ? "Güncellendi" : "Eklendi");
      setShowExpForm(false);
      await loadData();
    } catch (err) { toast.error(errMsg(err)); }
  };
  const deleteExp = async (row) => {
    if (!window.confirm(`${row.supplier_name} · ${MONTHS_FULL[row.month-1]} ${row.year} · ${formatTL(row.amount)} silinecek. Onaylıyor musunuz?`)) return;
    try { await api.delete(`/school-supplier-payments/${row.id}`); toast.success("Silindi"); await loadData(); }
    catch (err) { toast.error(errMsg(err)); }
  };

  const unmatchPayment = async (row) => {
    if (!window.confirm(`${row.student_name} için banka eşleşmesi kaldırılacak. Devam?`)) return;
    try { await api.post(`/school-payments/unmatch/${row.id}`); toast.success("Eşleşme kaldırıldı"); await loadData(); }
    catch (err) { toast.error(errMsg(err)); }
  };

  return (
    <>
      <PageHeader
        title="Okul Öğrenci Ödemeleri"
        subtitle={`${year} yılı öğrenci ödemeleri, okul tedarikçi giderleri ve aylık gelir-gider karşılaştırması`}
        testid="school-payments-header"
        actions={
          <div className="flex items-center gap-2 flex-wrap">
            <select data-testid="year-select" value={year} onChange={e => setYear(Number(e.target.value))} className="px-3 py-2 text-xs border border-[#E5E5E5] font-semibold">
              {[2024, 2025, 2026, 2027].map(y => <option key={y} value={y}>{y}</option>)}
            </select>
            <button data-testid="btn-upload-xlsx" onClick={() => { setUploadPreview(null); setUploadFile(null); setShowUpload(true); }}
              className="inline-flex items-center gap-2 border border-[#0020C2] text-[#0020C2] px-3 py-2 text-xs uppercase tracking-widest font-semibold hover:bg-[#0020C2] hover:text-white transition-colors">
              <FileSpreadsheet className="w-3.5 h-3.5" /> Excel Yükle
            </button>
            <button data-testid="btn-add-student-pay" onClick={openAddStudent} className="inline-flex items-center gap-2 bg-[#16A34A] text-white px-3 py-2 text-xs uppercase tracking-widest font-semibold hover:bg-[#15803D]">
              <Plus className="w-3.5 h-3.5" /> Öğrenci Ödemesi
            </button>
            <button data-testid="btn-add-exp" onClick={openAddExp} className="inline-flex items-center gap-2 bg-[#DC2626] text-white px-3 py-2 text-xs uppercase tracking-widest font-semibold hover:bg-[#B91C1C]">
              <Plus className="w-3.5 h-3.5" /> Okul Gideri
            </button>
          </div>
        }
      />

      {/* KPIs */}
      <div className="grid grid-cols-2 md:grid-cols-5 gap-3 mb-4">
        <div className="bg-white border border-[#E5E5E5] p-3">
          <div className="text-[10px] uppercase tracking-widest text-neutral-500 flex items-center gap-1"><TrendingUp className="w-3 h-3 text-[#16A34A]" /> Toplam Gelir</div>
          <div className="num font-bold text-xl mt-1 text-[#16A34A]">{formatTL(g.income || 0)}</div>
        </div>
        <div className="bg-white border border-[#E5E5E5] p-3">
          <div className="text-[10px] uppercase tracking-widest text-neutral-500 flex items-center gap-1"><TrendingDown className="w-3 h-3 text-[#DC2626]" /> Toplam Gider</div>
          <div className="num font-bold text-xl mt-1 text-[#DC2626]">{formatTL(g.expense || 0)}</div>
        </div>
        <div className="bg-white border border-[#E5E5E5] p-3">
          <div className="text-[10px] uppercase tracking-widest text-neutral-500">Net</div>
          <div className={`num font-bold text-xl mt-1 ${(g.net || 0) >= 0 ? "text-[#16A34A]" : "text-[#DC2626]"}`}>{formatTL(g.net || 0)}</div>
        </div>
        <div className="bg-white border border-[#E5E5E5] p-3">
          <div className="text-[10px] uppercase tracking-widest text-neutral-500">Öğrenci</div>
          <div className="num font-bold text-xl mt-1">{g.student_count || 0}</div>
          <div className="text-[10px] text-neutral-500">{g.income_count || 0} ödeme</div>
        </div>
        <div className="bg-white border border-[#E5E5E5] p-3">
          <div className="text-[10px] uppercase tracking-widest text-neutral-500">Faturalı</div>
          <div className="num font-bold text-xl mt-1 text-[#0020C2]">{g.invoiced_count || 0}</div>
          <div className="text-[10px] text-neutral-500">/ {g.income_count || 0}</div>
        </div>
      </div>

      {/* Tabs */}
      <div className="flex items-center gap-1 border-b border-[#E5E5E5] mb-4">
        {TABS.map(t => (
          <button key={t.key} data-testid={`tab-${t.key}`} onClick={() => setTab(t.key)}
            className={`flex items-center gap-2 px-4 py-2.5 text-xs uppercase tracking-widest font-semibold border-b-2 -mb-px transition-colors ${
              tab === t.key ? "border-[#0A0A0A] text-[#0A0A0A]" : "border-transparent text-neutral-500 hover:text-[#0A0A0A]"
            }`}>
            <t.icon className="w-3.5 h-3.5" /> {t.label}
          </button>
        ))}
      </div>

      {/* Search + month filter for the two data tabs */}
      {(tab === "students" || tab === "expenses") && (
        <div className="bg-white border border-[#E5E5E5] p-3 rounded-md mb-4 flex items-center gap-3 flex-wrap">
          <input data-testid="search" value={q} onChange={e => setQ(e.target.value)} placeholder="Ara..." className="flex-1 min-w-[200px] px-3 py-1.5 text-sm border border-[#E5E5E5]" />
          <div className="flex items-center gap-2">
            <Filter className="w-3.5 h-3.5 text-neutral-500" />
            <select data-testid="filter-month" value={monthFilter} onChange={e => setMonthFilter(e.target.value)} className="px-2 py-1.5 text-xs border border-[#E5E5E5]">
              <option value="all">Tüm aylar</option>
              {MONTHS_FULL.map((m, i) => <option key={i} value={i+1}>{m}</option>)}
            </select>
          </div>
        </div>
      )}

      {/* Tab: Students */}
      {tab === "students" && (
        <div className="bg-white border border-[#E5E5E5] rounded-md overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full text-xs">
              <thead className="bg-[#FAFAFA] border-b border-[#E5E5E5]">
                <tr>
                  {["İşlem","Öğrenci","Dönem","Yıl","Ay","Tutar (₺)","Fatura","Not"].map((h, i) => (
                    <th key={h} className={`px-3 py-2 text-[10px] uppercase tracking-widest text-neutral-500 font-semibold ${i === 5 ? "text-right" : "text-left"}`}>{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {filteredPayments.length === 0 && (
                  <tr><td colSpan={8} className="text-center py-8 text-neutral-500">Kayıt bulunamadı</td></tr>
                )}
                {filteredPayments.map((row, i) => (
                  <tr key={row.id} className="border-t border-[#F5F5F5] hover:bg-[#FAFAFA]">
                    <td className="px-3 py-1.5">
                      <div className="flex items-center gap-1">
                        <button data-testid={`edit-student-${i}`} onClick={() => openEditStudent(row)} className="p-1 hover:bg-[#F5F5F5]" title="Düzenle"><Pencil className="w-3 h-3" /></button>
                        <button data-testid={`del-student-${i}`} onClick={() => deleteStudent(row)} className="p-1 hover:bg-[#FEE2E2]" title="Sil"><Trash2 className="w-3 h-3 text-[#DC2626]" /></button>
                      </div>
                    </td>
                    <td className="px-3 py-1.5 font-semibold">{row.student_name}</td>
                    <td className="px-3 py-1.5 text-neutral-500">{row.academic_year}</td>
                    <td className="px-3 py-1.5 num">{row.year}</td>
                    <td className="px-3 py-1.5">{MONTHS_FULL[row.month-1]}</td>
                    <td className="px-3 py-1.5 num text-right font-bold text-[#16A34A]">{formatTL(row.amount)}</td>
                    <td className="px-3 py-1.5">
                      <span className={`text-[9px] uppercase tracking-widest font-semibold px-1.5 py-0.5 ${row.invoiced ? "bg-[#DCFCE7] text-[#16A34A]" : "bg-[#FEE2E2] text-[#DC2626]"}`}>
                        {row.invoiced ? "Kesildi" : "Yok"}
                      </span>
                      {row.bank_matched && (
                        <span className="ml-1 text-[9px] uppercase tracking-widest font-semibold px-1.5 py-0.5 bg-[#DBEAFE] text-[#0020C2] cursor-pointer" title="Banka eşleşmesi var (kaldırmak için tıkla)" onClick={() => unmatchPayment(row)}>
                          <Unlink className="w-2.5 h-2.5 inline" /> Banka
                        </span>
                      )}
                    </td>
                    <td className="px-3 py-1.5 text-neutral-500 text-[11px]">{row.notes || "-"}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* Tab: Expenses */}
      {tab === "expenses" && (
        <div className="bg-white border border-[#E5E5E5] rounded-md overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full text-xs">
              <thead className="bg-[#FAFAFA] border-b border-[#E5E5E5]">
                <tr>
                  {["İşlem","Tedarikçi","Yıl","Ay","Tutar (₺)","Tarih","Not"].map((h, i) => (
                    <th key={h} className={`px-3 py-2 text-[10px] uppercase tracking-widest text-neutral-500 font-semibold ${i === 4 ? "text-right" : "text-left"}`}>{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {filteredExpenses.length === 0 && (
                  <tr><td colSpan={7} className="text-center py-8 text-neutral-500">Manuel gider kaydı yok. Karşılaştırma sekmesinde araç faturalarındaki "OKUL" kalemleri de otomatik gösterilir.</td></tr>
                )}
                {filteredExpenses.map((row, i) => (
                  <tr key={row.id} className="border-t border-[#F5F5F5] hover:bg-[#FAFAFA]">
                    <td className="px-3 py-1.5">
                      <div className="flex items-center gap-1">
                        <button data-testid={`edit-exp-${i}`} onClick={() => openEditExp(row)} className="p-1 hover:bg-[#F5F5F5]" title="Düzenle"><Pencil className="w-3 h-3" /></button>
                        <button data-testid={`del-exp-${i}`} onClick={() => deleteExp(row)} className="p-1 hover:bg-[#FEE2E2]" title="Sil"><Trash2 className="w-3 h-3 text-[#DC2626]" /></button>
                      </div>
                    </td>
                    <td className="px-3 py-1.5 font-semibold">{row.supplier_name}</td>
                    <td className="px-3 py-1.5 num">{row.year}</td>
                    <td className="px-3 py-1.5">{MONTHS_FULL[row.month-1]}</td>
                    <td className="px-3 py-1.5 num text-right font-bold text-[#DC2626]">{formatTL(row.amount)}</td>
                    <td className="px-3 py-1.5 num text-neutral-500">{row.date}</td>
                    <td className="px-3 py-1.5 text-neutral-500 text-[11px]">{row.notes || "-"}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* Tab: Reconcile (Banka Eşleştir) */}
      {tab === "reconcile" && (
        <div className="space-y-4">
          <div className="bg-white border border-[#E5E5E5] rounded-md p-4 flex items-center justify-between gap-3 flex-wrap">
            <div className="max-w-3xl">
              <div className="font-heading font-bold text-lg mb-1">Banka Hareketlerinden Öğrenci Ödemesi Eşleştirme</div>
              <div className="text-xs text-neutral-500">Gelen banka hareketlerinin açıklamasını öğrenci adlarıyla karşılaştırır. Aynı öğrenci için tutar eşleşen ödeme varsa (tolerance ± ₺) otomatik eşleştirir. Onayınızdan sonra öğrenci ödemeleri "banka eşleşti" olarak işaretlenir.</div>
            </div>
            <div className="flex items-center gap-2">
              <label className="text-[10px] uppercase tracking-widest text-neutral-500 font-semibold">Tolerans ₺</label>
              <input data-testid="recon-tolerance" type="number" step="0.1" value={reconTolerance} onChange={e => setReconTolerance(Number(e.target.value))} className="w-20 px-2 py-1.5 text-xs border border-[#E5E5E5] num" />
              <button data-testid="recon-preview-btn" onClick={loadReconcile} disabled={reconLoading}
                className="px-4 py-2 text-xs uppercase tracking-widest font-semibold bg-[#0020C2] text-white hover:bg-[#0018A0] disabled:opacity-40 flex items-center gap-2">
                {reconLoading ? <RefreshCw className="w-3.5 h-3.5 animate-spin" /> : <Link2 className="w-3.5 h-3.5" />}
                {reconLoading ? "Tarıyor..." : "Eşleştirmeyi Tara"}
              </button>
            </div>
          </div>

          {reconcile && (
            <>
              <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                <div className="border border-[#E5E5E5] bg-white p-3">
                  <div className="text-[10px] uppercase tracking-widest text-neutral-500">Bulunan Eşleşme</div>
                  <div className="num font-bold text-lg text-[#16A34A]">{reconcile.matched_count}</div>
                </div>
                <div className="border border-[#E5E5E5] bg-white p-3">
                  <div className="text-[10px] uppercase tracking-widest text-neutral-500">Eşleşen Tutar</div>
                  <div className="num font-bold text-lg text-[#16A34A]">{formatTL(reconcile.matched_amount)}</div>
                </div>
                <div className="border border-[#E5E5E5] bg-white p-3">
                  <div className="text-[10px] uppercase tracking-widest text-neutral-500">Toplam Gelen Banka Hrk</div>
                  <div className="num font-bold text-lg">{reconcile.total_incoming_bank_txs}</div>
                </div>
                <div className="border border-[#E5E5E5] bg-white p-3">
                  <div className="text-[10px] uppercase tracking-widest text-neutral-500">Eşleşmeyen Ödeme</div>
                  <div className="num font-bold text-lg text-[#F59E0B]">{reconcile.unmatched_payments}</div>
                </div>
              </div>

              <div className="bg-white border border-[#E5E5E5] rounded-md overflow-hidden">
                <div className="px-4 py-3 bg-[#FAFAFA] border-b border-[#E5E5E5] flex items-center justify-between flex-wrap gap-2">
                  <div>
                    <div className="text-[10px] uppercase tracking-widest text-neutral-500 font-semibold">Önizleme</div>
                    <div className="text-xs">Seçili: <span className="num font-bold text-[#0020C2]">{reconSelected.size}</span> / {reconcile.matches.length}</div>
                  </div>
                  <button data-testid="recon-apply-btn" onClick={applyReconcile} disabled={reconSelected.size === 0}
                    className="px-4 py-2 text-xs uppercase tracking-widest font-semibold bg-[#16A34A] text-white hover:bg-[#15803D] disabled:opacity-40 flex items-center gap-2">
                    <Link2 className="w-3.5 h-3.5" /> {reconSelected.size} Eşleştirmeyi Uygula
                  </button>
                </div>
                <div className="overflow-x-auto">
                  <table className="w-full text-xs">
                    <thead className="bg-white border-b border-[#F5F5F5]">
                      <tr>
                        <th className="px-3 py-2 text-left w-8"><input type="checkbox" checked={reconSelected.size > 0 && reconSelected.size === reconcile.matches.length} onChange={e => setReconSelected(e.target.checked ? new Set(reconcile.matches.map(m => m.bank_tx_id)) : new Set())} /></th>
                        {["Banka Tarih","Banka Açıklama","Banka Tutar","→","Öğrenci","Ay","Öğrenci Tutar","Fark"].map(h => (
                          <th key={h} className="px-3 py-2 text-left text-[10px] uppercase tracking-widest text-neutral-500 font-semibold">{h}</th>
                        ))}
                      </tr>
                    </thead>
                    <tbody>
                      {reconcile.matches.length === 0 && (
                        <tr><td colSpan={9} className="text-center py-10 text-neutral-500 text-xs">Eşleşme bulunamadı. Banka hareketlerinde açıklama yoksa veya öğrenci adı geçmiyorsa eşleşmez.</td></tr>
                      )}
                      {reconcile.matches.map((m, i) => (
                        <tr key={m.bank_tx_id + m.student_payment_id} className="border-t border-[#F5F5F5] hover:bg-[#FAFAFA]">
                          <td className="px-3 py-1.5"><input data-testid={`recon-chk-${i}`} type="checkbox" checked={reconSelected.has(m.bank_tx_id)} onChange={() => toggleReconSel(m.bank_tx_id)} /></td>
                          <td className="px-3 py-1.5 num whitespace-nowrap">{m.bank_date}</td>
                          <td className="px-3 py-1.5 max-w-[280px] truncate" title={m.bank_desc}>{m.bank_desc}{m.bank_counterparty && <span className="text-neutral-500"> · {m.bank_counterparty}</span>}</td>
                          <td className="px-3 py-1.5 num text-right text-[#16A34A] font-semibold">{formatTL(m.bank_amount)}</td>
                          <td className="px-3 py-1.5 text-[#0020C2]"><Link2 className="w-3 h-3 inline" /></td>
                          <td className="px-3 py-1.5 font-semibold">{m.student_name}</td>
                          <td className="px-3 py-1.5">{MONTHS_FULL[m.student_month-1]} {m.student_year}</td>
                          <td className="px-3 py-1.5 num text-right">{formatTL(m.student_amount)}</td>
                          <td className={`px-3 py-1.5 num text-right ${m.amount_diff < 0.5 ? "text-neutral-400" : "text-[#F59E0B] font-semibold"}`}>{m.amount_diff > 0 ? formatTL(m.amount_diff) : "="}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            </>
          )}

          {!reconcile && !reconLoading && (
            <div className="bg-white border border-[#E5E5E5] p-12 text-center">
              <Landmark className="w-10 h-10 text-neutral-400 mx-auto mb-3" />
              <div className="font-heading font-bold text-sm mb-1">Henüz taranmadı</div>
              <div className="text-xs text-neutral-500">Yukarıdaki "Eşleştirmeyi Tara" butonuna basın. {year} yılı gelen banka hareketleri öğrenci ödemeleriyle otomatik karşılaştırılacak.</div>
            </div>
          )}
        </div>
      )}

      {/* Tab: Comparison */}
      {tab === "comparison" && summary && (
        <div className="bg-white border border-[#E5E5E5] rounded-md overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full text-xs">
              <thead className="bg-[#FAFAFA] border-b border-[#E5E5E5]">
                <tr>
                  <th className="px-3 py-2 text-left text-[10px] uppercase tracking-widest text-neutral-500 font-semibold">Ay</th>
                  <th className="px-3 py-2 text-right text-[10px] uppercase tracking-widest text-[#16A34A] font-semibold">Gelir</th>
                  <th className="px-3 py-2 text-right text-[10px] uppercase tracking-widest text-neutral-500 font-semibold">Ödeme Adet</th>
                  <th className="px-3 py-2 text-right text-[10px] uppercase tracking-widest text-[#0020C2] font-semibold">Faturalı</th>
                  <th className="px-3 py-2 text-right text-[10px] uppercase tracking-widest text-[#DC2626] font-semibold">Gider (Manuel)</th>
                  <th className="px-3 py-2 text-right text-[10px] uppercase tracking-widest text-[#F59E0B] font-semibold">Gider (Araç Fat. OKUL)</th>
                  <th className="px-3 py-2 text-right text-[10px] uppercase tracking-widest text-[#DC2626] font-semibold">Toplam Gider</th>
                  <th className="px-3 py-2 text-right text-[10px] uppercase tracking-widest text-[#0A0A0A] font-semibold">Net</th>
                </tr>
              </thead>
              <tbody>
                {summary.monthly.map(m => (
                  <tr key={m.month} className="border-t border-[#F5F5F5]">
                    <td className="px-3 py-2 font-semibold">{MONTHS_FULL[m.month-1]} {year}</td>
                    <td className="px-3 py-2 num text-right text-[#16A34A] font-bold">{formatTL(m.income)}</td>
                    <td className="px-3 py-2 num text-right text-neutral-500">{m.income_count}</td>
                    <td className="px-3 py-2 num text-right text-[#0020C2]">{m.invoiced_count}</td>
                    <td className="px-3 py-2 num text-right">{formatTL(m.expense_manual)}</td>
                    <td className="px-3 py-2 num text-right">{formatTL(m.expense_invoice)}</td>
                    <td className="px-3 py-2 num text-right text-[#DC2626] font-bold">{formatTL(m.expense)}</td>
                    <td className={`px-3 py-2 num text-right font-bold ${m.net >= 0 ? "text-[#16A34A]" : "text-[#DC2626]"}`}>{formatTL(m.net)}</td>
                  </tr>
                ))}
                <tr className="border-t-2 border-[#0A0A0A] bg-[#FAFAFA] font-bold">
                  <td className="px-3 py-3">TOPLAM</td>
                  <td className="px-3 py-3 num text-right text-[#16A34A]">{formatTL(g.income || 0)}</td>
                  <td className="px-3 py-3 num text-right">{g.income_count || 0}</td>
                  <td className="px-3 py-3 num text-right text-[#0020C2]">{g.invoiced_count || 0}</td>
                  <td className="px-3 py-3 num text-right">{formatTL(g.expense_manual || 0)}</td>
                  <td className="px-3 py-3 num text-right">{formatTL(g.expense_invoice || 0)}</td>
                  <td className="px-3 py-3 num text-right text-[#DC2626]">{formatTL(g.expense || 0)}</td>
                  <td className={`px-3 py-3 num text-right ${(g.net || 0) >= 0 ? "text-[#16A34A]" : "text-[#DC2626]"}`}>{formatTL(g.net || 0)}</td>
                </tr>
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* Upload Modal */}
      {showUpload && (
        <div className="fixed inset-0 bg-black/40 z-50 flex items-center justify-center p-4" onClick={() => setShowUpload(false)}>
          <div className="bg-white w-full max-w-4xl rounded-md shadow-lg max-h-[92vh] overflow-y-auto" onClick={e => e.stopPropagation()}>
            <div className="flex items-center justify-between px-6 py-4 border-b border-[#E5E5E5]">
              <div>
                <div className="text-[10px] uppercase tracking-widest text-neutral-500">Toplu İçe Aktarım</div>
                <div className="font-heading font-bold text-lg">Okul Ödemeleri Excel Yükleme</div>
              </div>
              <button data-testid="upload-close" onClick={() => setShowUpload(false)} className="p-1.5 hover:bg-[#F5F5F5]"><X className="w-4 h-4" /></button>
            </div>
            <div className="p-6 space-y-4">
              <div className="bg-[#F5F5F5] border border-[#E5E5E5] p-4 text-xs text-neutral-700 leading-relaxed">
                <div className="font-semibold text-[#0A0A0A] mb-2">📋 Beklenen Excel formatı:</div>
                <div className="space-y-1">
                  <div><span className="font-semibold">Sayfa Adı:</span> Akademik yıl (ör. <code>2024-2025</code>)</div>
                  <div><span className="font-semibold">R3:</span> ÖĞRENCİ ADI SOYADI · Aylar (AĞUSTOS...HAZİRAN, yıl öneki opsiyonel)</div>
                  <div><span className="font-semibold">Veri satırları:</span> Öğrenci ile ödeme tutarı + altında "FATURA DURUMU" satırı (EVET/HAYIR)</div>
                </div>
                <div className="mt-2 text-[#DC2626]">⚠️ Aynı akademik yıla ait mevcut kayıtlar silinip yenileriyle değiştirilir.</div>
              </div>

              <div>
                <label className="text-xs font-semibold uppercase tracking-widest text-neutral-600">Excel Dosyası *</label>
                <input data-testid="upload-file" type="file" accept=".xlsx" onChange={e => { setUploadFile(e.target.files?.[0] || null); setUploadPreview(null); }} className="mt-1.5 w-full text-sm" />
              </div>

              {!uploadPreview && (
                <div className="flex justify-end gap-2 pt-2">
                  <button data-testid="upload-preview" onClick={runUploadPreview} disabled={uploadLoading || !uploadFile}
                    className="px-4 py-2 text-xs uppercase tracking-widest font-semibold bg-[#0A0A0A] text-white hover:bg-[#0020C2] disabled:opacity-40">
                    {uploadLoading ? "Okunuyor..." : "Önizle"}
                  </button>
                </div>
              )}

              {uploadPreview && (
                <div className="space-y-4">
                  <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                    <div className="border border-[#E5E5E5] p-3">
                      <div className="text-[10px] uppercase tracking-widest text-neutral-500">Kayıt</div>
                      <div className="num font-bold text-lg">{uploadPreview.totals?.row_count || 0}</div>
                    </div>
                    <div className="border border-[#E5E5E5] p-3">
                      <div className="text-[10px] uppercase tracking-widest text-neutral-500">Öğrenci</div>
                      <div className="num font-bold text-lg text-[#0020C2]">{uploadPreview.totals?.students || 0}</div>
                    </div>
                    <div className="border border-[#E5E5E5] p-3">
                      <div className="text-[10px] uppercase tracking-widest text-neutral-500">Faturalı</div>
                      <div className="num font-bold text-lg text-[#16A34A]">{uploadPreview.totals?.invoiced_count || 0}</div>
                    </div>
                    <div className="border border-[#E5E5E5] p-3">
                      <div className="text-[10px] uppercase tracking-widest text-neutral-500">Toplam</div>
                      <div className="num font-bold text-lg text-[#16A34A]">{formatTL(uploadPreview.totals?.total_amount || 0)}</div>
                    </div>
                  </div>

                  <div className="border border-[#E5E5E5]">
                    <div className="px-3 py-2 bg-[#FAFAFA] text-[10px] uppercase tracking-widest text-neutral-500 font-semibold">Sayfa Bazlı Özet</div>
                    <table className="w-full text-xs">
                      <tbody>
                        {(uploadPreview.per_sheet || []).map((s, i) => (
                          <tr key={i} className="border-t border-[#F5F5F5]">
                            <td className="px-3 py-1.5 font-semibold">{s.sheet}</td>
                            <td className="px-3 py-1.5 text-right num">{s.rows} kayıt</td>
                            {s.note && <td className="px-3 py-1.5 text-[#DC2626]">{s.note}</td>}
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>

                  <div className="flex justify-between items-center gap-2 pt-4 border-t border-[#E5E5E5]">
                    <button onClick={() => setUploadPreview(null)} className="px-4 py-2 text-xs uppercase tracking-widest font-semibold border border-[#E5E5E5] hover:bg-[#F5F5F5]">← Farklı Dosya</button>
                    <div className="flex gap-2">
                      <button onClick={() => setShowUpload(false)} className="px-4 py-2 text-xs uppercase tracking-widest font-semibold border border-[#E5E5E5] hover:bg-[#F5F5F5]">Vazgeç</button>
                      <button data-testid="upload-commit" onClick={runUploadCommit} disabled={uploadLoading}
                        className="px-4 py-2 text-xs uppercase tracking-widest font-semibold bg-[#16A34A] text-white hover:bg-[#0F7A36] disabled:opacity-40">
                        {uploadLoading ? "İşleniyor..." : `✓ Sisteme İşle (${uploadPreview.totals?.row_count || 0} kayıt)`}
                      </button>
                    </div>
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Student payment form modal */}
      {showStudentForm && (
        <div className="fixed inset-0 bg-black/40 z-50 flex items-center justify-center p-4" onClick={() => setShowStudentForm(false)}>
          <div className="bg-white w-full max-w-md rounded-md shadow-lg" onClick={e => e.stopPropagation()}>
            <div className="flex items-center justify-between px-6 py-4 border-b border-[#E5E5E5]">
              <div className="font-heading font-bold text-lg">{editingStudent ? "Öğrenci Ödemesi Düzenle" : "Yeni Öğrenci Ödemesi"}</div>
              <button onClick={() => setShowStudentForm(false)} className="p-1.5 hover:bg-[#F5F5F5]"><X className="w-4 h-4" /></button>
            </div>
            <form onSubmit={submitStudent} className="p-6 space-y-3">
              <div>
                <label className="text-xs font-semibold uppercase tracking-widest text-neutral-600">Öğrenci Adı *</label>
                <input data-testid="stu-name" required value={studentForm.student_name} onChange={e => setStudentForm({...studentForm, student_name: e.target.value})} className="mt-1.5 w-full px-3 py-2 bg-white border border-[#E5E5E5] text-sm" />
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="text-xs font-semibold uppercase tracking-widest text-neutral-600">Yıl *</label>
                  <input data-testid="stu-year" type="number" required value={studentForm.year} onChange={e => setStudentForm({...studentForm, year: e.target.value})} className="mt-1.5 w-full px-3 py-2 bg-white border border-[#E5E5E5] text-sm num" />
                </div>
                <div>
                  <label className="text-xs font-semibold uppercase tracking-widest text-neutral-600">Ay *</label>
                  <select data-testid="stu-month" required value={studentForm.month} onChange={e => setStudentForm({...studentForm, month: Number(e.target.value)})} className="mt-1.5 w-full px-3 py-2 bg-white border border-[#E5E5E5] text-sm">
                    {MONTHS_FULL.map((m, i) => <option key={i} value={i+1}>{m}</option>)}
                  </select>
                </div>
              </div>
              <div>
                <label className="text-xs font-semibold uppercase tracking-widest text-neutral-600">Tutar (₺) *</label>
                <input data-testid="stu-amount" type="number" step="0.01" required value={studentForm.amount} onChange={e => setStudentForm({...studentForm, amount: e.target.value})} className="mt-1.5 w-full px-3 py-2 bg-white border border-[#E5E5E5] text-sm num" />
              </div>
              <div className="flex items-center gap-2">
                <input data-testid="stu-invoiced" type="checkbox" id="stu-invoiced-cb" checked={!!studentForm.invoiced} onChange={e => setStudentForm({...studentForm, invoiced: e.target.checked})} />
                <label htmlFor="stu-invoiced-cb" className="text-xs">Fatura kesildi mi?</label>
              </div>
              <div>
                <label className="text-xs font-semibold uppercase tracking-widest text-neutral-600">Not</label>
                <input value={studentForm.notes || ""} onChange={e => setStudentForm({...studentForm, notes: e.target.value})} className="mt-1.5 w-full px-3 py-2 bg-white border border-[#E5E5E5] text-sm" />
              </div>
              <div className="flex justify-end gap-2 pt-4 border-t border-[#E5E5E5]">
                <button type="button" onClick={() => setShowStudentForm(false)} className="px-4 py-2 text-xs uppercase tracking-widest font-semibold border border-[#E5E5E5] hover:bg-[#F5F5F5]">Vazgeç</button>
                <button data-testid="stu-submit" type="submit" className="px-4 py-2 text-xs uppercase tracking-widest font-semibold bg-[#16A34A] text-white hover:bg-[#15803D]">{editingStudent ? "Kaydet" : "Ekle"}</button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Expense form modal */}
      {showExpForm && (
        <div className="fixed inset-0 bg-black/40 z-50 flex items-center justify-center p-4" onClick={() => setShowExpForm(false)}>
          <div className="bg-white w-full max-w-md rounded-md shadow-lg" onClick={e => e.stopPropagation()}>
            <div className="flex items-center justify-between px-6 py-4 border-b border-[#E5E5E5]">
              <div className="font-heading font-bold text-lg">{editingExp ? "Okul Gideri Düzenle" : "Yeni Okul Gideri"}</div>
              <button onClick={() => setShowExpForm(false)} className="p-1.5 hover:bg-[#F5F5F5]"><X className="w-4 h-4" /></button>
            </div>
            <form onSubmit={submitExp} className="p-6 space-y-3">
              <div>
                <label className="text-xs font-semibold uppercase tracking-widest text-neutral-600">Tedarikçi Adı *</label>
                <input data-testid="exp-supplier" required value={expForm.supplier_name} onChange={e => setExpForm({...expForm, supplier_name: e.target.value})} className="mt-1.5 w-full px-3 py-2 bg-white border border-[#E5E5E5] text-sm" />
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="text-xs font-semibold uppercase tracking-widest text-neutral-600">Yıl *</label>
                  <input data-testid="exp-year" type="number" required value={expForm.year} onChange={e => setExpForm({...expForm, year: e.target.value})} className="mt-1.5 w-full px-3 py-2 bg-white border border-[#E5E5E5] text-sm num" />
                </div>
                <div>
                  <label className="text-xs font-semibold uppercase tracking-widest text-neutral-600">Ay *</label>
                  <select data-testid="exp-month" required value={expForm.month} onChange={e => setExpForm({...expForm, month: Number(e.target.value)})} className="mt-1.5 w-full px-3 py-2 bg-white border border-[#E5E5E5] text-sm">
                    {MONTHS_FULL.map((m, i) => <option key={i} value={i+1}>{m}</option>)}
                  </select>
                </div>
              </div>
              <div>
                <label className="text-xs font-semibold uppercase tracking-widest text-neutral-600">Tutar (₺) *</label>
                <input data-testid="exp-amount" type="number" step="0.01" required value={expForm.amount} onChange={e => setExpForm({...expForm, amount: e.target.value})} className="mt-1.5 w-full px-3 py-2 bg-white border border-[#E5E5E5] text-sm num" />
              </div>
              <div>
                <label className="text-xs font-semibold uppercase tracking-widest text-neutral-600">Tarih</label>
                <input type="date" value={expForm.date || ""} onChange={e => setExpForm({...expForm, date: e.target.value})} className="mt-1.5 w-full px-3 py-2 bg-white border border-[#E5E5E5] text-sm" />
              </div>
              <div>
                <label className="text-xs font-semibold uppercase tracking-widest text-neutral-600">Not</label>
                <input value={expForm.notes || ""} onChange={e => setExpForm({...expForm, notes: e.target.value})} className="mt-1.5 w-full px-3 py-2 bg-white border border-[#E5E5E5] text-sm" />
              </div>
              <div className="flex justify-end gap-2 pt-4 border-t border-[#E5E5E5]">
                <button type="button" onClick={() => setShowExpForm(false)} className="px-4 py-2 text-xs uppercase tracking-widest font-semibold border border-[#E5E5E5] hover:bg-[#F5F5F5]">Vazgeç</button>
                <button data-testid="exp-submit" type="submit" className="px-4 py-2 text-xs uppercase tracking-widest font-semibold bg-[#DC2626] text-white hover:bg-[#B91C1C]">{editingExp ? "Kaydet" : "Ekle"}</button>
              </div>
            </form>
          </div>
        </div>
      )}
    </>
  );
}
